import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AutoRunBadge from '../components/AutoRunBadge';
import { SearchIcon, ChevronsIcon, PlusIcon, NodesIcon } from '../components/icons';
import { isNewTabClick, openInNewTab } from '../utils/navigation';
import {
  SORT_OPTIONS,
  RAIL_SECTIONS,
  groupProjects,
  countProjects,
  projectKey,
  activityLabel,
  relativeTime,
} from './dashboardGroups';

// Collapse state + rail width persist per browser, following the repo's
// `hidenn.*` localStorage convention (hidenn.sidebarWidth, hidenn.autoRunSeen.*).
const SECTIONS_KEY = 'hidenn.dashSections';
const WIDTH_KEY = 'hidenn.dashSidebarWidth';
const COLLAPSED_KEY = 'hidenn.dashRailCollapsed';
const DEFAULT_WIDTH = 260;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;
// Wide enough for one emoji plus its count badge. Deliberately NOT derived from
// MIN_WIDTH — this is a different UI, not a narrow version of the same one.
const RAIL_WIDTH = 48;

// "Shared with me" starts collapsed: it is other people's work, and on a
// team it can be longer than everything above it put together.
const DEFAULT_OPEN = { starred: true, needsAttention: true, recents: true, shared: false };

// ONE derivation of where a project row goes, shared by the row's ctrl+click and
// by the `Open ↗` link inside it, so the two cannot disagree about the URL.
//
// `isShared` comes from the SECTION rather than the project (that is the
// authoritative signal in this component), and only decides the router state a
// same-tab navigation carries. A genuine new tab loses that state, which is fine:
// ProjectPage.load() re-derives the owner from listSharedProjects() when the
// project isn't in the caller's own list.
const projectPath = (project) => `/project/${project.projectId}`;
const projectLinkState = (project, isShared) =>
  isShared ? { shared: true, owner: project.ownerId } : undefined;

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

function readWidth() {
  try {
    const n = parseInt(localStorage.getItem(WIDTH_KEY), 10);
    if (Number.isFinite(n)) return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, n));
  } catch {
    /* ignore */
  }
  return DEFAULT_WIDTH;
}

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * The dashboard's left rail: a search button, an overflow menu, and the four
 * fixed project sections.
 *
 * Presentational — every mutation goes back up to Dashboard.js, which stays the
 * single owner of the project lists and of every modal. It no longer filters:
 * `onSearchOpen` raises the palette, which is where searching now happens.
 */
export default function DashboardSidebar({
  projects,
  shared,
  loading,
  onSearchOpen,
  sort,
  onSortChange,
  renamingId,
  renameValue,
  onRenameValueChange,
  onRenameSubmit,
  onRenameCancel,
  onStartRename,
  onOpenProject,
  // Bind the centre pane to this project's Dennis conversation. Optional so the
  // rail still renders standalone (its own test mounts it without one).
  onSelectConversation,
  onTogglePin,
  onShare,
  onCopy,
  onExport,
  onAddQualityCheck,
  onDelete,
  onLeave,
  // Release the centre pane back to the composer. Optional, like
  // `onSelectConversation` — the rail's own test mounts it without one.
  onNewProject,
  // Create an empty project and open the canvas on it, skipping the composer
  // entirely. Optional for the same reason as `onNewProject`, and Dashboard
  // additionally withholds it when the user has no valid license — this one
  // NAVIGATES onto the license-gated project route, so offering it would
  // manufacture an orphan project and then bounce the user to /admin.
  onBuildManually,
}) {
  const [open, setOpen] = useState(() => readJson(SECTIONS_KEY, DEFAULT_OPEN));
  const [width, setWidth] = useState(readWidth);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  // Keyed by projectKey(), NOT a bare projectId: an owned and a shared project
  // can carry the same id, and comparing ids alone opens both menus at once.
  const [menuKey, setMenuKey] = useState(null);
  // Which row is showing its summary. Keyed by projectKey() for the same reason
  // menuKey is: an owned and a shared project can carry the same projectId, and
  // a bare id would expand both at once.
  const [expandedKey, setExpandedKey] = useState(null);
  // Two flags gate the width tween. `resizing` turns it OFF (a transition would
  // lag the mousemove-driven drag); `animating` is on only for the duration of a
  // collapse/expand, and is what clips the content mid-tween.
  const [resizing, setResizing] = useState(false);
  const [animating, setAnimating] = useState(false);
  const renameInputRef = useRef(null);
  const widthRef = useRef(width);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    try {
      localStorage.setItem(SECTIONS_KEY, JSON.stringify(open));
    } catch {
      /* ignore */
    }
  }, [open]);

  useEffect(() => {
    if (renamingId) renameInputRef.current?.focus();
  }, [renamingId]);

  // A timer rather than onTransitionEnd: that event never fires when the two
  // widths happen to match, which would strand `overflow: hidden` on the rail
  // and clip the ⋯ / row menus for the rest of the session. Must outlast the
  // 160ms tween in Dashboard.css.
  useEffect(() => {
    if (!animating) return undefined;
    const t = setTimeout(() => setAnimating(false), 220);
    return () => clearTimeout(t);
  }, [animating]);

  function toggleSection(name) {
    setOpen((prev) => ({ ...prev, [name]: !prev[name] }));
  }

  // Written imperatively rather than from an effect, matching startResize's
  // mouseup write — a mount-firing effect would stamp the key for every user
  // who has never touched the toggle.
  function setRailCollapsed(next) {
    setCollapsed(next);
    setAnimating(true);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  }

  // Drag-to-resize, cloned from ProjectPage's .sidebar-resizer.
  function startResize(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;
    setResizing(true);
    function onMove(ev) {
      const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + ev.clientX - startX));
      setWidth(next);
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setResizing(false);
      try {
        localStorage.setItem(WIDTH_KEY, String(widthRef.current));
      } catch {
        /* ignore */
      }
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  const groups = groupProjects(projects, shared, { sort });
  const total = countProjects(groups);

  function renderRow(project, { isShared }) {
    const key = projectKey(project);
    const renaming = renamingId === project.projectId;
    const label = activityLabel(project);
    const expanded = expandedKey === key;
    // Every project predating the `description` field has none, and an empty
    // disclosure that expands to nothing is worse than no disclosure.
    const summary = (project.description || '').trim();
    return (
      // The detail cannot live inside `.dash-row` — that is a centred flex row,
      // so a summary would lay out as another column. The hover-reveal rules for
      // the star and ⋯ hang off this wrapper for the same reason: they must stay
      // visible while the detail below is open.
      <div key={key} className={`dash-row-wrap${expanded ? ' expanded' : ''}`}>
        <div
          className={`dash-row${project.unseen ? ' unseen' : ''}`}
          // Single click SELECTS an OWNED project: it loads that project's Dennis
          // conversation into the centre pane and discloses its summary. Double
          // click (and `Open ↗` below) still open the canvas.
          //
          // A summary-less owned row used to open on a single click, because there
          // was nothing to disclose and a dead click is worse than a navigation.
          // There is now something to do — bind the conversation — so it selects
          // like every other owned row instead of being the one that navigates.
          //
          // A SHARED project cannot have a conversation: `/api/agent-run` is
          // owner-only (there is no `?owner=` on it), so binding one would poll
          // into a 403 and show a permanently empty thread. Shared rows therefore
          // keep opening on a single click, which is also what their read-only
          // viewer is for.
          //
          // The row cannot BE a link: an anchor's default action fires on the
          // first click, which would navigate instead of binding. So it consults
          // `isNewTabClick` itself — ctrl/⌘+click and middle-click open the canvas
          // in a new tab, as they would on any other site. `onAuxClick` is what
          // delivers the middle button; `onClick` never sees it.
          onClick={(e) => {
            if (renaming || menuKey) return;
            if (isNewTabClick(e)) {
              openInNewTab(projectPath(project));
              return;
            }
            if (isShared) {
              onOpenProject(project);
              return;
            }
            onSelectConversation?.(project);
            if (summary) setExpandedKey(expanded ? null : key);
          }}
          onAuxClick={(e) => {
            if (renaming || menuKey) return;
            if (isNewTabClick(e)) {
              e.preventDefault();
              openInNewTab(projectPath(project));
            }
          }}
          onDoubleClick={() => {
            if (!renaming && !menuKey) onOpenProject(project);
          }}
        >
          <div className="dash-row-main">
            {renaming ? (
              <input
                ref={renameInputRef}
                className="dash-rename-input"
                value={renameValue}
                onChange={(e) => onRenameValueChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onRenameSubmit(project.projectId);
                  if (e.key === 'Escape') onRenameCancel();
                }}
                onBlur={() => onRenameSubmit(project.projectId)}
              />
            ) : (
              <span className="dash-row-name" title={project.name}>
                {project.unseen && <span className="dash-unseen-dot" aria-label="Unseen updates" />}
                {project.name}
              </span>
            )}
            <span className="dash-row-meta">
              {isShared
                ? `Shared by ${project.ownerEmail || 'a teammate'}`
                : label || relativeTime(project.updatedAt)}
            </span>
            <AutoRunBadge project={project} />
          </div>

          {!isShared && (
            <button
              className={`dash-star${project.pinned ? ' on' : ''}`}
              title={project.pinned ? 'Unstar' : 'Star'}
              aria-label={project.pinned ? 'Unstar project' : 'Star project'}
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin(project);
              }}
            >
              {project.pinned ? '★' : '☆'}
            </button>
          )}

          <div className="dash-row-menu-wrap" onClick={(e) => e.stopPropagation()}>
            <button
              className="dash-row-menu-btn"
              aria-label="Project actions"
              onClick={() => setMenuKey(menuKey === key ? null : key)}
            >
              ⋯
            </button>
            {menuKey === key && (
              <div className="dash-row-menu">
                {isShared ? (
                  <>
                    <button
                      onClick={() => {
                        setMenuKey(null);
                        onCopy(project, project.ownerId);
                      }}
                    >
                      Make a copy
                    </button>
                    <button
                      onClick={() => {
                        setMenuKey(null);
                        onExport(project, project.ownerId);
                      }}
                    >
                      Download a copy
                    </button>
                    <button
                      className="delete-option"
                      onClick={() => {
                        setMenuKey(null);
                        onLeave(project);
                      }}
                    >
                      Remove me
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setMenuKey(null);
                        onTogglePin(project);
                      }}
                    >
                      {project.pinned ? 'Unstar' : 'Star'}
                    </button>
                    <button
                      onClick={() => {
                        setMenuKey(null);
                        onStartRename(project);
                      }}
                    >
                      Rename
                    </button>
                    <button
                      onClick={() => {
                        setMenuKey(null);
                        onShare(project);
                      }}
                    >
                      Share
                    </button>
                    <button
                      onClick={() => {
                        setMenuKey(null);
                        onCopy(project);
                      }}
                    >
                      Make a copy
                    </button>
                    <button
                      onClick={() => {
                        setMenuKey(null);
                        onExport(project);
                      }}
                    >
                      Download a copy
                    </button>
                    <button
                      onClick={() => {
                        setMenuKey(null);
                        onAddQualityCheck(project);
                      }}
                    >
                      Add to Quality Check
                    </button>
                    <button
                      className="delete-option"
                      onClick={() => {
                        setMenuKey(null);
                        onDelete(project);
                      }}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {expanded && summary && (
          <div className="dash-row-detail">
            <p className="dash-row-summary">{summary}</p>
            {/* A real link, so ctrl/⌘+click, middle-click and "Copy link address"
                all work on it. `stopPropagation` still keeps the click off the
                row behind it, which would otherwise toggle this disclosure shut. */}
            <Link
              className="applink dash-row-open"
              to={projectPath(project)}
              state={projectLinkState(project, isShared)}
              onClick={(e) => e.stopPropagation()}
            >
              Open ↗
            </Link>
          </div>
        )}
      </div>
    );
  }

  // Every section renders, empty or not: the four are a fixed vocabulary, and a
  // section that vanishes when empty makes the rail's shape depend on your data
  // — you cannot tell "no starred projects" from "no Starred section".
  function renderSection(name, title, rows, { isShared = false, extra = null } = {}) {
    const isOpen = open[name];
    return (
      <section className="dash-section" key={name}>
        <header className="dash-section-head">
          <button
            className="dash-section-toggle"
            aria-expanded={isOpen}
            onClick={() => toggleSection(name)}
          >
            <span className={`dash-caret${isOpen ? ' open' : ''}`}>▸</span>
            {title}
            <span className="dash-section-count">{rows.length}</span>
          </button>
          {isOpen && extra}
        </header>
        {isOpen && (
          <div className="dash-section-body">
            {rows.length ? (
              rows.map((project) => renderRow(project, { isShared }))
            ) : (
              <p className="dash-section-empty">Nothing here yet</p>
            )}
          </div>
        )}
      </section>
    );
  }

  const sortControl = (
    <select
      className="dash-sort"
      value={sort}
      onChange={(e) => onSortChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      title="Sort projects"
    >
      {SORT_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );

  // Both branches below return an <aside> at the same position in the tree, so
  // React reuses the ONE DOM node across the collapse — which is what lets the
  // width tween run even though the two subtrees are otherwise unrelated.
  const railClass = [
    'dash-sidebar',
    collapsed ? 'collapsed' : '',
    resizing ? 'dash-sidebar--resizing' : '',
    animating ? 'dash-sidebar--animating' : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Collapsed is a DIFFERENT subtree, not the expanded one hidden by CSS: a
  // display:none search input is still focusable in some browsers, so hiding it
  // would leave a tab-order trap in a 48px strip. The strip is the toggle and
  // NOTHING else — every action and every section is one » click away, so a 48px
  // column of glyphs would be a second, worse copy of the rail it collapses.
  // Three things are therefore deliberately absent — the resizer (a col-resize
  // drag on 48px would fight the toggle and overwrite hidenn.dashSidebarWidth),
  // the persisted `width` (so expanding restores the user's drag width
  // untouched), and the ⋯ menus + their backdrop (nothing can be open here).
  if (collapsed) {
    return (
      <aside className={railClass} style={{ width: RAIL_WIDTH }} aria-label="Projects">
        <button
          className="dash-rail-toggle"
          aria-label="Expand projects rail"
          aria-expanded={false}
          title="Expand"
          onClick={() => setRailCollapsed(false)}
        >
          <ChevronsIcon direction="right" />
        </button>
      </aside>
    );
  }

  return (
    <aside className={railClass} style={{ width }}>
      <div className="dash-sidebar-head">
        {/* Row 1 is the brand line: wordmark, search, collapse. The ⋯ menu that
            used to sit here is gone — "All projects" is the nav item below, and
            Import / Delete All moved onto /projects, which is the surface that
            can actually show what they affect. */}
        <div className="dash-sidebar-head-actions">
          <h2 className="dash-brand">Dennis</h2>
          <button
            className="dash-search-btn"
            aria-label="Search projects"
            title="Search projects (⌘K)"
            onClick={onSearchOpen}
          >
            <SearchIcon />
          </button>
          {/* Last child, so the toggle sits flush with the rail's right edge —
              the side it collapses toward, and where the » sits to bring it back. */}
          <button
            className="dash-rail-toggle"
            aria-label="Collapse projects rail"
            aria-expanded={true}
            title="Collapse"
            onClick={() => setRailCollapsed(true)}
          >
            <ChevronsIcon direction="left" />
          </button>
        </div>
        {/* The centre pane is EITHER the composer or a bound conversation, and
            binding one is a single rail click — so without this there is no way
            back to the starting interface at all (the bound id is persisted, so
            not even a reload clears it). It sits above the Projects nav item
            because starting something new is the more common intent. */}
        {onNewProject && (
          <button className="dash-nav-new" onClick={onNewProject}>
            <PlusIcon className="dash-nav-new-icon" />
            New project
          </button>
        )}
        {/* Second, and deliberately the QUIETER of the two: describing the problem
            and letting Dennis build the pipeline is the common path, so it keeps the
            single filled CTA. This one skips the composer altogether — it creates an
            empty project and opens the canvas, for a pipeline you want to wire up
            by hand. */}
        {onBuildManually && (
          <button className="dash-nav-build" onClick={onBuildManually}>
            <NodesIcon className="dash-nav-build-icon" />
            Build it myself
          </button>
        )}
        {/* A `<Link>` rather than a button so ctrl/⌘+click opens it in a new tab.
            The two actions above stay buttons on purpose: "New project" only
            releases the centre-pane binding (no URL at all), and "Build it myself"
            creates the project before it can know the URL. */}
        <Link className="applink dash-nav-projects" to="/projects">
          Projects
        </Link>
      </div>

      <div className="dash-sidebar-body">
        {loading ? (
          // role/aria-label so the load is announced rather than being a silent
          // block of grey bars (and so tests can address it semantically).
          <div className="dash-skeletons" role="status" aria-label="Loading projects">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="dash-row-skeleton" />
            ))}
          </div>
        ) : total === 0 ? (
          // Four empty accordions is a worse first run than one sentence, so the
          // genuinely-empty case still replaces the whole body.
          <div className="dash-empty">
            <p>No projects yet</p>
            <span>Describe what you want to simulate to get started</span>
          </div>
        ) : (
          // Driven by the RAIL_SECTIONS table, whose four entries are this
          // accordion's fixed vocabulary — an empty one still renders.
          RAIL_SECTIONS.map((s) =>
            renderSection(s.name, s.title, groups[s.name], {
              isShared: s.name === 'shared',
              extra: s.name === 'recents' ? sortControl : null,
            })
          )
        )}
      </div>

      <div className="dash-sidebar-resizer" title="Drag to resize" onMouseDown={startResize} />
      {menuKey && <div className="menu-backdrop" onClick={() => setMenuKey(null)} />}
    </aside>
  );
}
